import React from "react";
import { mdiInformationOutline } from "@mdi/js";
import MdiIcon from "../../components/device/app/MdiIcon";
import Sheet from "../../components/layout/app/Sheet";
import { DEVICE_TYPES } from "../../constants/devices";
import { useAutomationFeature } from "../../automation/AutomationProvider";
import { actionKind } from "../../automation/actions";
import { sensorEvents } from "./create/draft";
import { Chips, OptionRow } from "./create/controls";
import { TypeIcon } from "./RunViews";

const ROWS = [
  { type: "timer", title: "Timer", text: "Turn it on or off after a while" },
  { type: "schedule", title: "Schedule", text: "At set times on the days you choose" },
  { type: "sun", title: "Sunrise/Sunset", text: "Before or after sunrise or sunset" },
  { type: "inching", title: "Inching", text: "Switch it back off a while after it turns on" },
  { type: "cycling", title: "Cycling", text: "On for a while, off for a while, repeat" },
  { type: "tap", title: "Tap to run", text: "Run it with other devices in one tap" },
  { type: "rule", title: "Use in a rule", text: "React when a door, motion, gas or smoke sensor changes" },
];

// A device's "+ Add" (app/screens/Automation/AddAutomationSheet.js): only what
// this device supports. Sensors start rules instead of being switched.
// onPick(type, { event }) opens the create flow; onDeviceSettings() opens the
// device's own settings.
export default function AddAutomationSheet({ open, device, onClose, onPick, onDeviceSettings }) {
  const { ready, device: capsDevice } = useAutomationFeature();
  if (!open) return null;
  const caps = capsDevice(device?.applianceId);
  const name = device?.name || caps?.name || "this device";
  const events = sensorEvents(caps || device);
  const controllable = Boolean(actionKind(device));
  const startsRules = Boolean(events.length && caps?.ruleTrigger);

  const rows = caps && controllable ? ROWS.filter((row) => ready(row.type) && (caps.types || []).includes(row.type)) : [];
  const onDevice = rows.some((row) => caps?.[row.type] === "device");

  return (
    <Sheet open={open} onClose={onClose} title={`Add to ${name}`}>
      <div className="hx-auto-sheet">
        <p className="hx-auto-sheet-text">
          {startsRules ? "Sensors start automations; they are not switched by them." : "Only what this device supports"}
        </p>
        {startsRules && ready("rule") ? (
          <div className="hx-auto-when">
            <div className="hx-auto-when-top">
              <TypeIcon type="rule" size={40} />
              <div>
                <p className="hx-auto-option-title">When this happens…</p>
                <p className="hx-auto-option-sub">Start a rule with it</p>
              </div>
            </div>
            <Chips
              label="Start a rule when it"
              options={events.map((e) => ({ key: e.key, label: e.label }))}
              value={null}
              onChange={(event) => onPick("rule", { event })}
            />
          </div>
        ) : null}
        <div className="hx-auto-options">
          {rows.map((row) => {
            const place = row.type === "inching" || row.type === "cycling" ? caps[row.type] : null;
            const settingsHere = place === "device" && caps.deviceType === DEVICE_TYPES.WATER_LEVEL_CONTROLLER;
            return (
              <OptionRow
                key={row.type}
                type={row.type}
                title={row.title}
                badge={place === "device" ? "On device" : place === "cloud" ? "Cloud" : null}
                text={
                  place === "device"
                    ? settingsHere
                      ? "Uses the controller's own cyclic on and off times, in Motor Settings"
                      : "Set on the device itself; it can't be changed here yet"
                    : row.text
                }
                disabled={place === "device" && !settingsHere}
                onPress={() => (place === "device" ? onDeviceSettings() : onPick(row.type, {}))}
              />
            );
          })}
        </div>
        {!rows.length && !startsRules ? (
          <p className="hx-auto-none">{caps ? "This device can't be used in automations yet." : "This device isn't shared with you."}</p>
        ) : null}
        {onDevice ? (
          <p className="hx-auto-info">
            <MdiIcon path={mdiInformationOutline} size={18} />
            "On device" runs on the device itself, so it keeps working when the internet is down.
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}
